/**
 * How a logical channel name becomes the name on the wire.
 *
 * Shared between the publisher and the subscriber because they must agree
 * exactly: a name computed two ways is a name one end publishes to and the
 * other never hears, with no error at either.
 *
 * Reached through the `@thalamos/common/realtime` subpath rather than the root
 * barrel. A browser-side consumer declares no ambient global types at all, and
 * the barrel's error helpers need V8's `Error.captureStackTrace` — so importing
 * the root here would oblige that package to take on Node's globals just to
 * compute a channel name.
 */
/**
 * The markers Pusher reads to decide what kind of channel a name describes.
 *
 * It matches them at the **start** of the name and nowhere else, so a prefix
 * placed in front of one turns an authorised channel into a public one —
 * silently, because subscribing then succeeds without any authorisation being
 * asked for. Longest first, so `private-encrypted-` is not mistaken for
 * `private-`.
 */
const CHANNEL_KINDS = ["private-encrypted-", "private-", "presence-"];
/**
 * The wire name for `channel`, namespaced by `prefix`.
 *
 * The prefix is what lets concurrent environments share one Pusher
 * application. It is inserted **after** any kind marker rather than in front
 * of it, so `private-person.1` under prefix `pr-9` is
 * `private-pr-9-person.1` — still recognisably private — and not
 * `pr-9-private-person.1`, which Pusher would treat as a public channel that
 * anybody holding the application key may join.
 */
export const wireChannelName = (prefix, channel) => {
    if (prefix === undefined || prefix === "") {
        return channel;
    }
    const kind = CHANNEL_KINDS.find((marker) => channel.startsWith(marker));
    return kind === undefined
        ? `${prefix}-${channel}`
        : `${kind}${prefix}-${channel.slice(kind.length)}`;
};
/**
 * The logical name behind a wire name — the inverse of `wireChannelName`.
 *
 * Returns `undefined` where the name is not one this prefix produces, so a
 * caller refuses rather than guessing.
 */
export const logicalChannelName = (prefix, wireName) => {
    const kind = CHANNEL_KINDS.find((marker) => wireName.startsWith(marker));
    const marker = kind ?? "";
    const remainder = wireName.slice(marker.length);
    if (prefix === undefined || prefix === "") {
        return `${marker}${remainder}`;
    }
    const namespace = `${prefix}-`;
    return remainder.startsWith(namespace)
        ? `${marker}${remainder.slice(namespace.length)}`
        : undefined;
};
//# sourceMappingURL=realtimeChannelName.js.map